import type { AdminRuleset } from '../core/types';

// Starter scoring shapes, shared by the creation wizard and the owner control panel
// so the two can't drift. NOT the Phase-3 named-preset system — a sensible set of
// starting points written to the league ruleset (/api/mgmt/ruleset). The owner can
// still fine-tune the raw numbers in the classic admin scoring tab.

// veto_threshold is now a PERCENTAGE of players (veto_threshold_pct); the backend
// derives the live count. Starters use round %s that map to the old 5/4/3-of-10 feel.
export type ScoringNumbers = Pick<
  AdminRuleset,
  'budget' | 'track_min' | 'track_max' | 'negative_multiplier' | 'veto_penalty' | 'popularity_bonus' | 'popularity_tie_bonus'
> & { veto_threshold_pct: number };

export interface ScoringPreset {
  key: string;
  name: string;
  veto: boolean; // the preset's default veto on/off (seeds, but doesn't own, the veto toggle)
  nums: ScoringNumbers;
}

export const SCORING_PRESETS: ScoringPreset[] = [
  { key: 'standard', name: 'Standard', veto: true, nums: { budget: 20, track_min: 0, track_max: 20, negative_multiplier: 2, veto_threshold_pct: 50, veto_penalty: 10, popularity_bonus: 3, popularity_tie_bonus: 1 } },
  { key: 'relaxed', name: 'Relaxed', veto: true, nums: { budget: 30, track_min: -3, track_max: 10, negative_multiplier: 2, veto_threshold_pct: 40, veto_penalty: 8, popularity_bonus: 3, popularity_tie_bonus: 1 } },
  { key: 'ruthless', name: 'Ruthless', veto: false, nums: { budget: 15, track_min: -5, track_max: 15, negative_multiplier: 3, veto_threshold_pct: 30, veto_penalty: 12, popularity_bonus: 4, popularity_tie_bonus: 2 } },
];

const NUM_KEYS = Object.keys(SCORING_PRESETS[0].nums) as (keyof ScoringNumbers)[];

/** e.g. "20 pts · up to 20/track · no down-votes". Veto is shown separately (own toggle). */
export function scoringLine(n: Pick<ScoringNumbers, 'budget' | 'track_max' | 'track_min' | 'negative_multiplier'>): string {
  return [`${n.budget} pts`, `up to ${n.track_max}/track`, n.track_min < 0 ? `down-votes ${n.negative_multiplier}×` : 'no down-votes'].join(' · ');
}

/** Which preset a live ruleset matches on the scoring numbers (null = the owner has custom numbers). */
export function matchPreset(r: Partial<ScoringNumbers>): ScoringPreset | null {
  return SCORING_PRESETS.find((p) => NUM_KEYS.every((k) => p.nums[k] === r[k])) ?? null;
}
